import { toLonLat } from "./mercator";
import { baseUrl } from "../Layers/layerBasics";
import { apiFetch } from "../serverApi";

// Names a spot on the map from /nearplace (GetNearPlace.java): the nearest street and
// postcode in ukmap.places. Used for the dropped pin's card and for the name the place
// search shows for a point chosen on the map (long-press, current location).

// The nearest street is only named when it is this close; then the postcode
const STREET_MAX_M = 200;
const POSTCODE_MAX_M = 500;

// The words for what /nearplace found, e.g. "Near Vyse Street, B18"
export const describeSpot = (near) => {
    if (!near) return null;
    const {street, postcode} = near;
    if (street && street.dist_m<=STREET_MAX_M) {
        const district = street.postcode_district || (postcode && postcode.name ? postcode.name.split(' ')[0] : null);
        return {label:'Near '+street.name+(district ? ', '+district : ''), name:street.name+(district ? ', '+district : ''),
            area:street.populated_place};
    }
    if (postcode && postcode.dist_m<=POSTCODE_MAX_M) {
        return {label:'Near '+postcode.name, name:postcode.name, area:postcode.populated_place};
    }
    const town = (street && street.populated_place) || (postcode && postcode.populated_place);
    return town ? {label:'Near '+town, name:town, area:null} : null;
};

// The overview's name for a place picked from the search, given what is nearest its point
// (get_point_circle's details.nearplace): a postcode gets the nearest street in front of
// it ("Vyse Street, B18 6HF"), an area or town the nearest street and postcode district
// ("Vyse Street, B18"), and anything else (a building, a street) keeps the name it was
// picked by. Until the nearest places are known, and for a name with no category (one
// from a share link or saved location), the picked name.
const TOWN_OR_AREA = ['City', 'Town', 'Village', 'Hamlet', 'Area', 'Place'];
const FULL_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/;

export const pickedPlaceName = (picked, near) => {
    if (!picked || !picked.name) return '';
    if (!near || picked.category===undefined) return picked.name;
    if (TOWN_OR_AREA.includes(picked.category)) {
        const spot = describeSpot(near);
        return spot ? spot.name : picked.name;
    }
    if ((!picked.category || picked.category==='Postcode') && FULL_POSTCODE.test(picked.name)) {
        const {street} = near;
        return street && street.dist_m<=STREET_MAX_M ? street.name+', '+picked.name : picked.name;
    }
    return picked.name;
};

// The line under the overview's title for a place picked from the search: the area or
// town's own name when pickedPlaceName has named it by the nearest street ("Islington"
// under "Collins Yard, N1"), else null, and the overview shows the ward
export const pickedPlaceArea = (picked, near) => {
    if (!picked || !picked.name || !TOWN_OR_AREA.includes(picked.category)) return null;
    const name = pickedPlaceName(picked, near);
    return name && name!==picked.name ? picked.name : null;
};

// The postcode the marker is in, for the overview's link to the homes there: the postcode
// picked from the search, else the nearest postcode when it is close enough to be the
// marker's own (a postcode's homes are rarely more than this from its centre), else null
const MARKER_POSTCODE_MAX_M = 150;
export const markerPostcode = (picked, near) => {
    if (picked && (!picked.category || picked.category==='Postcode') && FULL_POSTCODE.test(picked.name || '')) {
        return picked.name;
    }
    const postcode = near && near.postcode;
    if (postcode && postcode.name && postcode.dist_m<=MARKER_POSTCODE_MAX_M && FULL_POSTCODE.test(postcode.name)) {
        return postcode.name;
    }
    return null;
};

export const lookUpNearPlace = async (point) => {
    try {
        const res = await apiFetch('/nearplace', {
            mode:'cors',
            method:'POST',
            credentials:'include',
            headers:{'Content-Type':'application/json', 'ngrok-skip-browser-warning':'true'},
            body:JSON.stringify({x:point.x, y:point.y}),
        });
        const data = await res.json();
        return data && !data.error ? data : null;
    }
    catch (e) {
        console.log(e);
        return null;
    }
};

export const latLonText = (point) => {
    const [lon, lat] = toLonLat([point.x, point.y]);
    return lat.toFixed(4)+', '+lon.toFixed(4);
};
